import React, { useEffect, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import {
  ClinicalPageShell,
  SignalBar,
  SectionPanel,
  ClinicalDataGrid,
  tokens,
  fmtNumber,
} from "../../shared/clinical-design";
import MembershipTrendChart from "../components/MembershipTrendChart";
import PlanMixPanel from "../components/PlanMixPanel";
import { getMembershipAnalytics } from "../services/membershipAnalyticsService";

/** Texto ▲/▼ con tono para deltas en celdas. */
const DeltaText = ({ value, suffix = "" }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: tokens.fontMono,
      fontVariantNumeric: "tabular-nums",
      fontSize: 13,
      color: value >= 0 ? tokens.ok : tokens.crit,
    }}
  >
    {value >= 0 ? "▲" : "▼"} {fmtNumber(Math.abs(value))}
    {suffix}
  </Typography>
);

const centerColumns = [
  { key: "centerName", header: "Center" },
  {
    key: "members",
    header: "Members",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.members),
  },
  {
    key: "sharePct",
    header: "Share",
    align: "right",
    mono: true,
    render: (r) => `${r.sharePct.toFixed(1)}%`,
  },
  {
    key: "newMembers",
    header: "New members YTD",
    align: "right",
    mono: true,
    render: (r) => fmtNumber(r.newMembers),
  },
  {
    key: "netMoM",
    header: "Net MoM",
    align: "right",
    render: (r) => <DeltaText value={r.netMoM} />,
  },
  {
    key: "churnRate",
    header: "Churn",
    align: "right",
    mono: true,
    render: (r) => (
      <Typography
        component="span"
        sx={{
          fontFamily: tokens.fontMono,
          fontVariantNumeric: "tabular-nums",
          fontSize: 13,
          color: r.churnRate > 2.5 ? tokens.crit : r.churnRate > 1.8 ? tokens.warn : tokens.ink,
        }}
      >
        {r.churnRate.toFixed(1)}%
      </Typography>
    ),
  },
  {
    key: "avgPmpm",
    header: "Avg revenue PMPM",
    align: "right",
    mono: true,
    render: (r) => `$${fmtNumber(r.avgPmpm)}`,
  },
];

/**
 * Module J — Membership Analytics & Retention.
 * Convierte el roster en inteligencia de membresía: tendencia neta,
 * churn por centro, mix de planes. No requiere AdvancedMD.
 */
export default function MembershipAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const result = await getMembershipAnalytics();
        if (active) setData(result);
      } catch (e) {
        // Fallback total: el servicio ya degrada a fake data por loader.
        if (active) setData(null);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const s = data?.signals;
  const signals = s
    ? [
        { label: "Total members", value: fmtNumber(s.totalMembers), trend: "as of May 2026" },
        {
          label: "Net growth MoM",
          value: fmtNumber(s.netMoM),
          tone: s.netMoM < 0 ? "crit" : "ok",
          trend: s.netMoM < 0 ? "▼" : "▲",
          hint: "Adds minus drops vs prior month",
        },
        {
          label: "Churn rate",
          value: `${s.churnRate.toFixed(1)}%`,
          tone: s.churnRate > 2 ? "warn" : "ok",
          hint: "Monthly disenrollments over prior-month membership",
        },
        {
          label: "Avg revenue PMPM",
          value: `$${fmtNumber(s.avgRevenuePmpm)}`,
          tone: "info",
          hint: "Average premium per member per month",
        },
        { label: "New members YTD", value: fmtNumber(s.newMembersYtd), tone: "info" },
      ]
    : [];

  return (
    <ClinicalPageShell
      title="Membership Analytics"
      subtitle="Roster intelligence: net growth, churn and plan mix by center. Early disenrollment signals without waiting on AdvancedMD."
      moduleTag="Module J"
      devPreview
    >
      <SignalBar signals={signals} sx={{ mb: 2 }} />

      <Stack direction={{ xs: "column", lg: "row" }} spacing={2} sx={{ mb: 2 }}>
        <SectionPanel title="Membership trend — last 12 months" sx={{ flex: 7, minWidth: 0 }}>
          <MembershipTrendChart data={data?.trend || []} loading={loading} />
        </SectionPanel>
        <SectionPanel title="Payer / plan mix" sx={{ flex: 5, minWidth: 0 }}>
          <PlanMixPanel planMix={data?.planMix} loading={loading} />
        </SectionPanel>
      </Stack>

      <SectionPanel title="Members by center" contentSx={{ p: 0 }}>
        <ClinicalDataGrid
          columns={centerColumns}
          rows={data?.centers || []}
          rowKey={(r) => r.centerName}
          loading={loading}
          emptyMessage="No membership data available for this period."
          maxHeight={420}
          sx={{ border: 0 }}
        />
      </SectionPanel>
      <Box sx={{ height: 8 }} />
    </ClinicalPageShell>
  );
}
